import {
	ProgressService,
	ActionService,
	EnvironmentService
} from 'phoenix/services';
import {
	Task,
	TaskStatus,
	TaskResult,
	IMenuItem,
	TaskAction
} from 'sharebase/domain';
import { MenuItemFactory } from 'sharebase/factories/menuItemFactory';
import {
	ActionRepository,
	ToastService,
	TrackingService
} from 'sharebase/services';
import { ToolbarService } from 'sharebase-app/services/appToolBarService';
import { TasksDataStore } from 'sharebase-app/dataStores/tasksDataStore';
import { TaskCommentDialog } from 'sharebase-app/components/taskCommentDialog/taskCommentDialog';
import { ConfirmDialog } from 'sharebase/components/confirmDialog/confirmDialog';
import { 
	GridEmptyState,
	GridInitSort 
} from 'sharebase/components/grid/grid';
import { SortableColumns } from 'sharebase-app/components/tasks-grid/tasks-grid';
import { DetailsPanelService } from 'sharebase-app/services/detailsPanelService';

require('./tasks.styles');

let template = require('./tasks.template');

export class TasksController implements ng.IComponentController {
	public static $inject = [
		'$mdMedia',
		'$q',
		'$timeout',
		'pxActionService',
		'pxEnvironmentService',
		'pxProgressService',
		'sbActionRepository',
		'sbMenuItemFactory',
		'sbToastService',
		'sbaToolbarService',
		'sbaTasksDataStore',
		'sbConfirmDialog',
		'sbaTaskCommentDialog',
		'sbaDetailsPanelService',
		'sbTrackingService',
		'gettextCatalog'
	];

	public tabNames: { myAssignedTasks:string, myCreatedTasks:string, historicalTasks:string };
	public get showCards() {
		return this.pxEnvironmentService.touchEnabled && !this.$mdMedia('min-width: 1367px');
	}

	public subscriptions = [];
	public actionsForAssignedTasks:IMenuItem[] = [];
	public actionsForCreatedTasks:IMenuItem[] = [];

	public historicalTasksLoaded = false;
	public openTasksAssignedToMeLoaded = false;
	public openTasksICreatedLoaded = false;

	public historicalEmptyState:GridEmptyState = {
		icon: 'assignment_turned_in',
		text: this.gettextCatalog.getString('There are no tasks to display.'),
		subtext: this.gettextCatalog.getString("Once a task is done, you'll be able to find it here.")
	}
	public assignedToMeEmptyState:GridEmptyState = {
		icon: 'assignment_turned_in',
		text: this.gettextCatalog.getString("Congratulations!"),
		subtext: this.gettextCatalog.getString("You're all caught up on your tasks.")
	}
	public assignedToOthersEmptyState:GridEmptyState = {
		icon: 'assignment_turned_in',
		text: this.gettextCatalog.getString("Good News!"),
		subtext: this.gettextCatalog.getString("If you created tasks, they've all been completed.")
	}

	public historyTasksGridInitSort:GridInitSort = {
		query: {
			order: SortableColumns.dateCompleted,
			filter: ''
		},
		filterDirection: true
	}
	public openTasksGridInitSort:GridInitSort = {
		query: {
			order: SortableColumns.dueDate,
			filter: ''
		},
		filterDirection: false
	}

	constructor(
		private $mdMedia:ng.material.IMedia,
		private $q:ng.IQService,
		private $timeout:ng.ITimeoutService,
		private pxActionService:ActionService,
		private pxEnvironmentService:EnvironmentService,
		private pxProgressService:ProgressService,
		private sbActionRepository:ActionRepository,
		private sbMenuItemFactory:MenuItemFactory,
		private sbToastService:ToastService,
		private sbaToolbarService:ToolbarService,
		public sbaTasksDataStore:TasksDataStore,
		private sbConfirmDialog: ConfirmDialog,
		private sbaTaskCommentDialog:TaskCommentDialog,
		private sbaDetailsPanelService:DetailsPanelService,
		private sbTrackingService:TrackingService,
		private gettextCatalog:ng.gettext.gettextCatalog
	) {
		this.sbaToolbarService.title = this.gettextCatalog.getString('My Tasks');
		this.sbaDetailsPanelService.hideDetailView = true;
		this.sbaDetailsPanelService.close();

		this.tabNames = {
			myAssignedTasks: gettextCatalog.getString('Assigned to Me'),
			myCreatedTasks: gettextCatalog.getString('Created by Me'),
			historicalTasks: gettextCatalog.getString('History')
		};

		this.loadMyAssignedTasks();
		this.loadMyCreatedTasks();
		this.craftActions();

		this.subscriptions.push(
			...this.pxActionService.subscribe<Task>([
				[TaskAction.QuickApprove1, (task, event) => this.completeTask(TaskAction.QuickApprove1, task, event)],
				[TaskAction.QuickApprove2, (task, event) => this.completeTask(TaskAction.QuickApprove2, task, event)],
				[TaskAction.ApproveWithComment, (task, event) => this.completeTask(TaskAction.ApproveWithComment, task, event)],
				[TaskAction.QuickDeny1, (task, event) => this.completeTask(TaskAction.QuickDeny1, task, event)],
				[TaskAction.QuickDeny2, (task, event) => this.completeTask(TaskAction.QuickDeny2, task, event)],
				[TaskAction.DenyWithComment, (task, event) => this.completeTask(TaskAction.DenyWithComment, task, event)],
				[TaskAction.Cancel, (task, event) => this.cancelTask(task, event)],
			])
		);
	}
	
	public $onDestroy() {
		this.trackTabChange('');
		this.subscriptions.forEach(unsubscribe => unsubscribe());
		this.sbaDetailsPanelService.hideDetailView = false;
	}
	
	private cancelTask(task:Task, event:ng.IAngularEvent) {
		let title = this.gettextCatalog.getString('Would you like to cancel this task?');
		let detail = this.gettextCatalog.getString('When you cancel a task, the assigned user will no longer be able to complete the previously requested action.');
		let confirmText = this.gettextCatalog.getString('Cancel Task');
		let cancelText = this.gettextCatalog.getString('Dismiss');

		this.sbConfirmDialog.open(event, detail, title, confirmText, cancelText)
			.then(confirmed => {
				if (confirmed) {
					task.cancel()
						.then(() => {
							this.sbToastService.showSimple(this.gettextCatalog.getString('The task was cancelled.'));
						})
						.catch(() => {
							this.sbToastService.showSimple(this.gettextCatalog.getString('Unable to cancel task.'))
						});
				}
			});
	}

	private completeTask(action:TaskAction, task:Task, event:ng.IAngularEvent) {
		switch(action) {
			case TaskAction.QuickApprove1:
			case TaskAction.QuickApprove2:
			case TaskAction.ApproveWithComment:
				task.result = TaskResult.Positive;
				break;
			default:
				task.result = TaskResult.Negative;
		}

		return this.retrieveCompletionComment(action, event)
			.then(comment => {
				this.pxProgressService.visible = true;
				return task.complete(comment);
			})
			.then(() => {
				this.sbToastService.showSimple(this.gettextCatalog.getString('The task has been successfully completed.'));
			})
			.catch(e => {
				if (e) {
					this.sbToastService.showSimple(this.gettextCatalog.getString('The task was unable to be completed.'));
				}
			})
			.finally(() => this.pxProgressService.visible = false);
	}

	private craftActions() {
		let approvalItem = this.sbMenuItemFactory.createNested('Approve Options', this.gettextCatalog.getString('Approve'), 'check');
		approvalItem.menu.addItem(this.sbActionRepository.createFromAction(this.sbActionRepository.getAction(TaskAction.QuickApprove1)));
		approvalItem.menu.addItem(this.sbActionRepository.createFromAction(this.sbActionRepository.getAction(TaskAction.QuickApprove2)));
		approvalItem.menu.addItem(this.sbActionRepository.createFromAction(this.sbActionRepository.getAction(TaskAction.ApproveWithComment)));
		approvalItem.addValidator((target:Task) => target.status === TaskStatus.Open);

		let denialItem = this.sbMenuItemFactory.createNested('Deny Options', this.gettextCatalog.getString('Deny'), 'close');
		denialItem.menu.addItem(this.sbActionRepository.createFromAction(this.sbActionRepository.getAction(TaskAction.QuickDeny1)));
		denialItem.menu.addItem(this.sbActionRepository.createFromAction(this.sbActionRepository.getAction(TaskAction.QuickDeny2)));
		denialItem.menu.addItem(this.sbActionRepository.createFromAction(this.sbActionRepository.getAction(TaskAction.DenyWithComment)));
		denialItem.addValidator((target:Task) => target.status === TaskStatus.Open);

		let cancelAction = this.sbActionRepository.createFromAction(this.sbActionRepository.getAction(TaskAction.Cancel));
		cancelAction.addValidator((target:Task) => target.status === TaskStatus.Open);
	
		this.actionsForAssignedTasks.push(approvalItem, denialItem);
		this.actionsForCreatedTasks.push(cancelAction);
  	}



	public loadHistoricalTasks() {
		//Nested in a timeout since this is only called when switching to the history tab and we
		//want a delay for the tab switching animation to complete
		let delay = this.pxEnvironmentService.isIE ? 0: 600; //IE has animations removed so it feels like it's unresponsive
		this.$timeout(() => {
			this.sbaTasksDataStore.loadHistoricalTasks()
			.catch(() => {
				this.sbToastService.showSimple(this.gettextCatalog.getString('An error occurred while loading your completed tasks.'))
			})
			.finally(() => {
				this.historicalTasksLoaded = true;
			});
		}, delay);
	}
	
	private loadMyAssignedTasks() {
		let p = this.sbaTasksDataStore.loadOpenTasksAssignedToMe()
			.catch(() => {
				this.sbToastService.showSimple(this.gettextCatalog.getString('An error occurred while loading tasks assigned to you.'))
			})
			.finally(() => {
				this.openTasksAssignedToMeLoaded = true;
			});

		this.pxProgressService.enqueue(p);
	  }
	
	private loadMyCreatedTasks() {    
		this.sbaTasksDataStore.loadOpenTasksICreated()
			.catch(() => {
				this.sbToastService.showSimple(this.gettextCatalog.getString('An error occurred while loading the tasks you created.'))
			})
			.finally(() => {
				this.openTasksICreatedLoaded = true;
			});
	}

	private retrieveCompletionComment(action:TaskAction, event:ng.IAngularEvent) {
		let comment = ''
		if (action !== TaskAction.QuickApprove1 && action !== TaskAction.QuickDeny1) {
			comment = this.sbActionRepository.getAction(action).label;
		}
		let p = this.$q.resolve(comment);
		if (action === TaskAction.ApproveWithComment || action === TaskAction.DenyWithComment) {
			p = this.sbaTaskCommentDialog.open(event, action);
		}
		return p;
	}

	private trackTabChange(tabName:string) {
		this.sbTrackingService.addCustomDimension('task grid tab', tabName);
	}
}

export const TasksComponent:ng.IComponentOptions = {
	template,
	controller: TasksController,
	controllerAs: 'vm'
}



// WEBPACK FOOTER //
// ./src/sharebase-app/components/tasks/tasks.ts